import { readTaskDetails, type Label, type Project, type SyncTable, type Task } from '@timato/shared';
import { db } from '@/db/db';
import { teamApi } from './api';
import { useTeam } from './store';

/**
 * Team-plane rows are edited through /v1/teams, never the outbox. `saveRow` calls this first; when it returns true the
 * row was a shared one: the API was called, the local mirror updated, and the personal write path must not run.
 */
export async function routeTeamWrite(table: SyncTable, row: Project | Task | Label): Promise<boolean> {
  const teamId = await teamIdOf(table, row);
  if (!teamId) return false;
  switch (table) {
    case 'projects': { const p = row as Project; await teamApi.updateProject(teamId, p.id, { name: p.name, color: p.color, weeklyTargetSec: p.weeklyTargetSec, labelIds: p.labelIds, archived: !!p.archivedAt }); await db.projects.put(p); break; }
    case 'tasks': {
      const t = row as Task;
      const exists = await db.tasks.get(t.id);
      if (!exists) { const { taskId } = await teamApi.createTask(teamId, t.projectId, { name: t.name, estimateSec: t.estimateSec, labelIds: t.labelIds, details: readTaskDetails(t) }); await db.tasks.put({ ...t, id: taskId }); }
      else if (t.deletedAt) { await teamApi.deleteTask(teamId, t.id); await db.tasks.delete(t.id); }
      else { await teamApi.updateTask(teamId, t.id, { name: t.name, estimateSec: t.estimateSec, labelIds: t.labelIds, details: readTaskDetails(t), finished: !!t.finishedAt, position: t.position }); await db.tasks.put(t); }
      break;
    }
    case 'labels': { const l = row as Label; await teamApi.updateLabel(teamId, l.id, { name: l.name, tint: l.tint, retired: !!l.retiredAt }); await db.labels.put(l); break; }
    default: return false;
  }
  void useTeam.getState().refresh();
  return true;
}

async function teamIdOf(table: SyncTable, row: Project | Task | Label): Promise<string | null> {
  if (table === 'projects' || table === 'labels') return (row as Project | Label).teamId ?? null;
  if (table === 'tasks') return (await db.projects.get((row as Task).projectId))?.teamId ?? null;
  return null;
}
